import axios, { AxiosRequestConfig } from 'axios';
import { addDate } from 'easit--utils';
import { ApiError, CancelError, prepareAxiosConfig, TicketListTimeFilterRequest } from '../api/apiBase';
import { createGroupedApi, IGroupedApi } from '../api/apiCommon';
import { ITicketDetailForVGResponse, ITicketListForVGResponse } from '../dtos/ticketForVirtualGamesDtos';
import { IDateTimeFormater } from '../services/dateFormater';

export enum TicketStatusVG {
    Open = "Open",
    Winning = "Winning",
    Lost = "Lost",
    Cancelled = "Cancelled",
    Rejected = "Rejected"
}

export enum TicketTypeForVG {
    Single = "Single",
    Combi = "Combi",
    System = "System",
    Split = "Split"
}

export interface ITicketListForVGParams {
    timeStamp?: number;
    indexOfPage?: number;
    rowsOfPage?: number;
    timeFilter?: TicketListTimeFilterRequest;
    ticketState?: TicketStatusVG;
    playerId: number;
}

export interface ITicketDetailForVGParams {
    barcode: string;
}


export interface IVirtualGamesApi {
    getTicketListForVGAsync: (params: ITicketListForVGParams) => Promise<ITicketListForVGResponse>;
    getTicketDetailForVGAsync: (params: ITicketDetailForVGParams) => Promise<ITicketDetailForVGResponse | null>;
    toDate(date: string | null): Date; //IApiDate2StringConverter
}

export interface IConnectVirtualGamesApiParams {
    dateTimeFormater: IDateTimeFormater;
    baseUrl: string;
    token: string;
}


export function connectVirtualGamesGroupedApi(apiParams: IConnectVirtualGamesApiParams): IGroupedApi<IVirtualGamesApi> {
    return createGroupedApi(token => {
        return connectVirtualGamesApi(apiParams, prepareAxiosConfig(token, false));
    });
}

export function connectVirtualGamesApi(apiParams: IConnectVirtualGamesApiParams, defaultConfig?: AxiosRequestConfig): IVirtualGamesApi {

    const dateToString = apiParams.dateTimeFormater.toString;
    const toDate = apiParams.dateTimeFormater.toDate;

    const prepareRequestConfigForVG = (): AxiosRequestConfig => {
        const config = { ...defaultConfig };
        if (!config.headers) {
            config.headers = {};
        }
        config.headers["Verify"] = "sport " + apiParams.token;
        return config;
    }

    function processError(error: any, errorHandler: (reason: any) => void) {
        if (axios.isCancel(error)) {
            errorHandler(new CancelError());
            return;
        };
        if (error instanceof Error) {
            errorHandler(new ApiError(error.message));
            return;
        }
        if (typeof (error) === 'string') {
            errorHandler(new ApiError(error));
            return;
        }
        errorHandler(new ApiError("An error in request"/*, error*/));
    }

    const getTicketListForVGAsync = (params: ITicketListForVGParams): Promise<ITicketListForVGResponse | null> => {

        let dateFrom: Date = undefined;
        switch (params.timeFilter) {
            //case TicketListTimeFilterRequest.All: dateFrom = addDate(new Date(), "day", -1); break;
            case TicketListTimeFilterRequest.Day: dateFrom = addDate(new Date(), "day", -1); break;
            case TicketListTimeFilterRequest.Month: dateFrom = addDate(new Date(), "month", -1); break;
            case TicketListTimeFilterRequest.ThreeDays: dateFrom = addDate(new Date(), "day", -3); break;
            case TicketListTimeFilterRequest.Today: const today = new Date(); dateFrom = new Date(today.getFullYear(), today.getMonth(), today.getDate()); break;
            case TicketListTimeFilterRequest.TwoWeeks: dateFrom = addDate(new Date(), "week", -2); break;
            case TicketListTimeFilterRequest.Week: dateFrom = addDate(new Date(), "week", -1); break;
        }

        return new Promise<ITicketListForVGResponse>((f, r) => {
            axios
                .post(
                    `${apiParams.baseUrl}/list`,
                    {
                        Token: apiParams.token,
                        pageSize: params.rowsOfPage,
                        pageIndex: params.indexOfPage,
                        createdFrom: dateFrom && dateToString(dateFrom),
                        //TicketState: params.ticketState,
                        //EvalState: params.evalState,
                    },
                    prepareRequestConfigForVG()
                )
                .then(response => {
                    if (response.data.resultOk) {
                        f(response.data);
                        return;
                    }
                    throw Error("ResultOk is false");
                })
                .catch(error => {
                    processError(error, r);
                });
        });
    }

    const getTicketDetailForVGAsync = (params: ITicketDetailForVGParams): Promise<ITicketDetailForVGResponse | null> => {

        return new Promise<ITicketDetailForVGResponse | null>((f, r) => {
            axios
                .get(
                    `${apiParams.baseUrl}/ticket/${params.barcode}`,
                    prepareRequestConfigForVG()
                )
                .then(response => {
                    if (response.data.resultOk) {
                        f(response.data);
                        return;
                    }
                    throw Error("ResultOk is false");
                })
                .catch(error => {
                    if (error.response && error.response.status == 404) {
                        f(null);
                        return;
                    }
                    processError(error, r);
                });
        });
    }

    return {
        getTicketListForVGAsync,
        getTicketDetailForVGAsync,
        toDate
    }
}
